import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (sessionUser) => {
    if (!sessionUser) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("user_id", sessionUser.id)
        .single();

      if (error) throw error;
      setUser({ ...data, email: sessionUser.email });
    } catch (err) {
      // Graceful fallback if the backend trigger hasn't finished writing the user profile yet
      setUser({
        user_id: sessionUser.id,
        email: sessionUser.email,
        name: sessionUser.user_metadata?.full_name || sessionUser.email?.split("@")[0] || "User",
        role: "user"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Initial Check
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) fetchProfile(session.user);
      else setLoading(false);
    });

    // 2. Listen for logins and logouts passively (this triggers instantly upon Login.jsx success)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
       if (_event === "SIGNED_IN") {
         fetchProfile(session?.user);
       } else if (_event === "SIGNED_OUT") {
         setUser(null);
       }
    });

    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const refreshUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) await fetchProfile(session.user);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
